export function useValidation() {
  const emailCharacters = "'abcdefghijklmnopqrstuvwxyz0123456789.@!#$%&'*+-/=?^_`{|}~'"
  const notAllowedCharacters = "'!#$%&'*+/=?^%_`{|}~'" // Especially not allowed in domain part
  const validStreetChars = "abcdefghijklmnopqrstuvwxyzäöüß0123456789 .-'"
  const houseNumberChars = 'abcdefghijklmnopqrstuvwxyzäöüß0123456789-/ '
  const validPhoneChars = '0123456789+/- '
  const vatIdChars = 'de0123456789'
  const nameChars = 'abcdefghijklmnopqrstuvwxyzäöüß0123456789- '
  const offerStore = useOfferStore()
  const { currentCity } = storeToRefs(offerStore)
  // Helperfunction to validate examples like postmaster@[255.255.255.0]
  function isValidIPv4(ipv4Address: string): boolean {
    const blocks = ipv4Address.split('.')
    if (blocks.length !== 4) return false
    for (const block of blocks) {
      const num = Number(block)
      if (isNaN(num) || num < 0 || num > 255) return false
    }
    return true
  }

  // Helperfunction to validate examples like postmaster@[IPv6:2001:0db8:85a3:0000:0000:8a2e:0370:7334]
  function isValidIPv6(ipv6Address: string): boolean {
    const parts = ipv6Address.split(':')
    const hasDoubleColon = ipv6Address.includes('::')
    if (hasDoubleColon) {
      const totalParts = ipv6Address.replace('::', ':').split(':').length
      if (totalParts > 8) return false // Too many parts even after compression
    } else if (parts.length !== 8) return false // Must have exactly 8 parts without "::"

    return parts.every(part => part.length <= 4 && /^[0-9a-fA-F]*$/.test(part))
  }

  // Validation for firstname, lastname, companyname
  const isNameValidated = (name: string): boolean => {
    if (name.length >= 2 && [...name.toLowerCase().trim()].every(char => nameChars.includes(char)))
      return true
    return false
  }

  const isEmailValidated = (email: string): boolean => {
    if (email.includes('@') && email.split('@').length == 2) {
      const [localPart, domainPart] = email.split('@')

      if (
        [...email.toLowerCase().trim()].every(char => emailCharacters.includes(char)) &&
        email.length >= 5 &&
        localPart.length > 0 &&
        domainPart.includes('.') &&
        domainPart.split('.')[0] &&
        email.slice(email.lastIndexOf('.') + 1).length > 0 &&
        !domainPart.split('').some(char => notAllowedCharacters.includes(char)) &&
        email.indexOf('..') == -1 &&
        (domainPart[0] !== '[' ||
          (domainPart[0] === '[' &&
            domainPart.endsWith(']') &&
            (isValidIPv4(domainPart.slice(1, -1)) || isValidIPv6(domainPart.slice(1, -1)))))
      ) {
        return true
      }
    }
    return false
  }

  const isStreetValidated = (street: string): boolean => {
    if (
      [...street.toLowerCase().trim()].every(char => validStreetChars.includes(char)) &&
      street.length >= 1 &&
      street.charAt(0) &&
      street.length < 50 &&
      street.split(' ').every(part => part.length >= 1) &&
      street.split(' ').every(part => part.split('-').every(subPart => subPart.length >= 1))
    ) {
      return true
    }
    return false
  }

  const isHouseNumberValidated = (houseNumber: string): boolean => {
    if (
      [...houseNumber.toLowerCase().trim()].every(char => houseNumberChars.includes(char)) &&
      houseNumber.length >= 1 &&
      houseNumber.length < 10 &&
      houseNumber.split(/[-/]/).length <= 2 &&
      houseNumber.split(/[-/]/).every(part => part.length >= 1 && part.length < 5) &&
      /^\d/.test(houseNumber)
    ) {
      return true
    }
    return false
  }

  const isZipCodeValidated = (zipCode: string): boolean => {
    if (zipCode.length === 5 && !isNaN(Number(zipCode)) && currentCity.value?.zip === zipCode) {
      return true
    }
    return false
  }

  const isBillingZipCodeValidated = (zipCode: string): boolean => {
    if (zipCode.length === 5 && !isNaN(Number(zipCode))) {
      return true
    }
    return false
  }

  const isDeliveryCityValidated = (city: string): boolean => {
    if (
      city.length >= 2 &&
      city.length < 50 &&
      /^[a-zA-Z]/.test(city) &&
      city.split(' ').every(part => part.length >= 1) &&
      city.split(' ').every(part => part.split('-').every(subPart => subPart.length >= 1)) &&
      currentCity.value?.name === city
    ) {
      return true
    }
    return false
  }

  const isInvoiceCityValidated = (city: string): boolean => {
    if (
      city.length >= 2 &&
      city.length < 50 &&
      /^[a-zA-Z]/.test(city) &&
      city.split(' ').every(part => part.length >= 1) &&
      city.split(' ').every(part => part.split('-').every(subPart => subPart.length >= 1))
    ) {
      return true
    }
    return false
  }

  const isPhoneNumberValidated = (phoneNumber: string): boolean => {
    if (
      [...phoneNumber.trim()].every(char => validPhoneChars.includes(char)) &&
      phoneNumber.length >= 4 &&
      phoneNumber.length < 20
    ) {
      return true
    }
    return false
  }

  const isVatIdValidated = (vatId: string): boolean => {
    if (
      [...vatId.toLowerCase().trim()].every(char => vatIdChars.includes(char)) &&
      vatId.length === 11 &&
      vatId.startsWith('DE')
    ) {
      return true
    }
    return false
  }

  return {
    isNameValidated,
    isEmailValidated,
    isStreetValidated,
    isHouseNumberValidated,
    isZipCodeValidated,
    isDeliveryCityValidated,
    isInvoiceCityValidated,
    isPhoneNumberValidated,
    isVatIdValidated,
    isBillingZipCodeValidated,
  }
}
